import React from 'react';
import { Box, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import DonutChart from '../../../../../components/charts/donutChart';
import { EXERCISE_TARGETS } from '../../../../../constants';
import { calculatePercentage, formatDateTimeDisplay } from '../../../../../utils/base';

const MonthlyCalendar = ({
    currentMonth = new Date().getMonth() + 1,
    currentYear = new Date().getFullYear(),
    onDateClick,
    monthlyData = {},
    selectedDate = null,
    isDidim = false,
    sx = {},
    ...props
}) => {
    const theme = useTheme();
    const { t } = useTranslation();

    // 월의 첫 번째 날과 마지막 날 계산
    const firstDay = new Date(currentYear, currentMonth - 1, 1);
    const lastDay = new Date(currentYear, currentMonth, 0);
    const daysInMonth = lastDay.getDate();
    const firstDayOfWeek = firstDay.getDay(); // 0 = 일요일

    // 달력 날짜 생성
    const calendarDays = [];

    // 이전 달의 빈 날짜들
    for (let i = 0; i < firstDayOfWeek; i++) {
        calendarDays.push({ day: null, isCurrentMonth: false });
    }

    // 현재 달의 날짜들
    for (let day = 1; day <= daysInMonth; day++) {
        const isToday = day === new Date().getDate() &&
            currentMonth === new Date().getMonth() + 1 &&
            currentYear === new Date().getFullYear();
        const isSelected = selectedDate === day;

        // 미래 날짜 확인
        const currentDate = new Date();
        const isFutureDate = new Date(currentYear, currentMonth - 1, day) > currentDate;

        // 해당 날짜의 실제 데이터 찾기
        const dayRecord = monthlyData.dailyData?.find(record => {
            const recordDate = new Date(record.date);
            return recordDate.getDate() === day;
        });

        calendarDays.push({
            day,
            isCurrentMonth: true,
            isToday,
            isSelected,
            isFutureDate,
            dayRecord
        });
    }

    // 실제 필요한 주수 계산 (현재까지의 날짜들로)
    const currentWeeks = Math.ceil(calendarDays.length / 7);

    // 다음 달의 빈 날짜들 (달력이 6주를 채우도록)
    const remainingDays = 42 - calendarDays.length;
    for (let i = 0; i < remainingDays; i++) {
        calendarDays.push({ day: null, isCurrentMonth: false });
    }

    // 실제 필요한 행 수 계산 (현재까지의 주수 사용)
    const actualWeeks = Math.min(currentWeeks, 6);

    return (
        <Box
            sx={{
                borderRadius: '20px',
                display: 'flex',
                flexDirection: 'column',
                gap: '10px',
                width: '100%',
                ...sx,
            }}
            {...props}
        >
            {/* 달력 그리드 */}
            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(7, 1fr)',
                    gridTemplateRows: `repeat(${actualWeeks}, 1fr)`,
                    padding: '0px',
                }}
            >
                {calendarDays.map((date, index) => (
                    <Box
                        key={index}
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'flex-start',
                            borderRadius: '15px',
                            cursor: date.day && !date.isFutureDate ? 'pointer' : 'default',
                            opacity: date.isFutureDate ? 0.5 : 1,
                        }}
                        onClick={() => date.day && !date.isFutureDate && onDateClick?.(date.day, currentMonth, currentYear)}
                    >
                        {date.day && (
                            <>
                                <DonutChart
                                    data={[
                                        {
                                            label: t('didim'),
                                            value: date.dayRecord ? calculatePercentage(date.dayRecord.totalSteps || 0, EXERCISE_TARGETS.STEPS) : 0,
                                            color: isDidim ? theme.palette.figma.didim : theme.palette.figma.steps,
                                            radius: 15,
                                            strokeWidth: 3
                                        },
                                        {
                                            label: t('calories'),
                                            value: date.dayRecord ? calculatePercentage(date.dayRecord.calories || 0, EXERCISE_TARGETS.CALORIES) : 0,
                                            color: theme.palette.figma.calorieRed,
                                            radius: 12,
                                            strokeWidth: 3
                                        },
                                        {
                                            label: t('time'),
                                            value: date.dayRecord ? calculatePercentage(date.dayRecord.totalTime || 0, EXERCISE_TARGETS.ACTIVITY_TIME) : 0,
                                            color: theme.palette.figma.time,
                                            radius: 9,
                                            strokeWidth: 3
                                        },
                                    ]}
                                    size={40}
                                    centerRadius={6}
                                    sx={{
                                        mt: '10px',
                                    }}
                                />

                                {/* 날짜 텍스트 */}

                                <Box
                                    sx={{
                                        backgroundColor: date.isSelected ? theme.palette.figma.Red_didim : 'transparent',
                                        borderRadius: '39px',
                                        padding: '5px 0',
                                        display: 'flex',
                                        justifyContent: 'center',
                                        alignItems: 'center',
                                        alignSelf: 'stretch',
                                        gap: '10px',
                                    }}
                                >
                                    <Typography
                                        sx={{
                                            fontSize: '12px',
                                            color: date.isSelected ? theme.palette.figma.buttonText : theme.palette.figma.white[60],
                                            fontWeight: 400,
                                            lineHeight: 'normal',
                                            letterSpacing: '-0.36px',
                                            textAlign: 'center',
                                        }}
                                    >
                                        {date.isSelected
                                            ? formatDateTimeDisplay(new Date(currentYear, currentMonth - 1, date.day), 'monthDay')
                                            : date.day}
                                    </Typography>
                                </Box>
                            </>
                        )}
                    </Box>
                ))}
            </Box>
        </Box>
    );
};

export default MonthlyCalendar;
